import { useEffect, useRef } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import GreenhouseTwin from "./GreenhouseTwin"
import HardwareShowcase from "./HardwareShowcase"
import { chaacFeatureGroups, aeroponicFeatures } from "./feederProducts"
import { feederArticles } from "./feederArticles"
import "./feederLanding.css"
import "./landingShowcase.css"
import "./publicShowcase.css"
import "./guideShowcase.css"
import "./twinShowcase.css"

export const feederHome = (hostname = window.location.hostname) =>
  ["feeder.at", "www.feeder.at"].includes(hostname) ? "/" : "/feeder"

function PublicShell({ children, title = "FEEDER · Pflanzen verstehen. Präzise bewässern.", className = "" }) {
  const location = useLocation()
  const scrollRoot = useRef(null)
  const home = feederHome()
  useEffect(() => {
    const previous = document.title
    document.title = title
    return () => { document.title = previous }
  }, [title])
  useEffect(() => {
    const root = scrollRoot.current
    const id = location.hash.slice(1)
    const target = id && document.getElementById(id)
    if (target && root && root.contains(target)) {
      target.scrollIntoView?.({ block: "start" })
      if (id === "main-content") target.focus({ preventScroll: true })
    }
    else if (root) root.scrollTop = 0
  }, [location.pathname, location.hash, location.key])
  return <div className={`feeder-marketing${className ? ` ${className}` : ""}`} ref={scrollRoot}>
    <Link className="fm-skip" to={{ pathname: location.pathname, search: location.search, hash: "#main-content" }}>Zum Inhalt</Link>
    <header className="fm-header">
      <Link className="fm-brand" to={home} aria-label="FEEDER Startseite"><span className="fm-brand-icon" aria-hidden="true">F</span> FEEDER</Link>
      <nav aria-label="Hauptnavigation">
        <Link to={`${home}#systeme`}>Systeme</Link>
        <Link to="/digital-twin" aria-current={location.pathname === "/digital-twin" ? "page" : undefined}>Digitaler Twin</Link>
        <Link to="/ratgeber" aria-current={location.pathname === "/ratgeber" || location.pathname === "/blog" || location.pathname.startsWith("/post/") ? "page" : undefined}>Ratgeber</Link>
        <Link className="fm-nav-login" to="/anmelden">Anmelden <span aria-hidden="true">↗</span></Link>
      </nav>
    </header>
    <main id="main-content" tabIndex="-1">{children}</main>
    <footer className="fm-footer">
      <Link className="fm-brand" to={home}>FEEDER</Link>
      <p>Vom Messwert zum besseren Verständnis.</p>
      <div><Link to="/ratgeber">Ratgeber</Link><a href="mailto:philovescoding@gmail.com">Kontakt</a><Link to="/impressum">Impressum</Link><Link to="/anmelden">Anmelden</Link></div>
      <small>© {new Date().getFullYear()} FEEDER · feeder.at</small>
    </footer>
  </div>
}

function ArticleCards({ articles = feederArticles, featured = false }) {
  return <div className={`fm-articles${featured ? " fm-articles-featured" : ""}`}>
    {articles.map((article, i) => <Link className="fm-article-card" key={article.slug} to={`/post/${article.slug}`}>
      <div className={`fm-article-art fm-art-${i % 3}`} aria-hidden="true"><span>{article.category}</span><b>{String(i + 1).padStart(2, "0")}</b><div className="fm-art-orbit"/></div>
      <div className="fm-article-copy"><span className="fm-eyebrow">{article.category} · Grundlagen</span><h3>{article.title}</h3><p>{article.excerpt}</p><span className="fm-text-link">Beitrag lesen <span aria-hidden="true">↗</span></span></div>
    </Link>)}
  </div>
}

export default function FeederLanding() {
  const featured = [feederArticles.find(a => a.slug === "aeroponik-tropfengroesse-luftfeuchte-bewaesserung"), ...feederArticles.filter(a => ["vpd-warum-dampfdruck-wachstum-steuert", "crop-steering-vwc-substrat"].includes(a.slug))].filter(Boolean)
  return <PublicShell className="fm-landing" title="FEEDER · Pflanzen verstehen. Bis zur Wurzel.">
    <section className="fl-hero" aria-labelledby="landing-title">
      <img className="fl-hero-image" src="/feeder-assets/landing-basil-hero.webp" alt="" decoding="async"/>
      <div className="fl-hero-content">
        <p className="fl-kicker"><span aria-hidden="true"/> FEEDER / Pflanzen. Daten. Zusammenhänge.</p>
        <h1 id="landing-title">Pflanzen<br/>verstehen.<br/><span>Bis zur Wurzel.</span></h1>
        <p className="fl-hero-lead">Klima und Substrat mit Chaac. Lokale Sprühsteuerung und Wurzelraummessung mit dem FEEDER Aeroponic Controller.</p>
        <div className="fl-actions">
          <Link className="fl-button" to={`${feederHome()}#systeme`}>Systeme entdecken <span aria-hidden="true">↓</span></Link>
          <Link className="fl-link" to="/digital-twin">Digitalen Twin öffnen <span aria-hidden="true">↗</span></Link>
        </div>
      </div>
      <div className="fl-hero-base"><span>VON DER LUFT BIS ZUM WURZELRAUM</span></div>
    </section>

    <section className="fl-aero fl-section" id="systeme" aria-labelledby="aero-title">
      <div className="fl-section-label"><span>01 / FEEDER Aeroponic Controller</span><span>Entwicklung & Erprobung</span></div>
      <div className="fl-section-heading" id="aeroponic">
        <h2 id="aero-title">Unter den Blättern<br/>beginnt die <span>Technik.</span></h2>
        <p>Die Pumpe arbeitet lokal. Eine eigene Messstrecke macht Feuchte und Temperatur im Wurzelraum nachvollziehbar. Sprühsteuerung und Beobachtung bleiben klar getrennte Aufgaben.</p>
      </div>
      <HardwareShowcase product="aeroponic"/>
      <div className="fl-aero-bottom">
        <div className="fl-aero-statement"><p>Fördern.<br/>Sprühen.<br/><span>Beobachten.</span></p><Link className="fl-link" to="/digital-twin">Funktionsweise erkunden <span aria-hidden="true">↗</span></Link></div>
        <div className="fl-disclosures" aria-label="Aeroponic-Technik im Detail">
          {aeroponicFeatures.map(feature => <details key={feature.title}><summary>{feature.title}</summary><p>{feature.text}</p></details>)}
        </div>
      </div>
    </section>

    <section className="fl-chaac fl-section" id="chaac" aria-labelledby="chaac-title">
      <div className="fl-section-label"><span>02 / Klima + Substrat</span><span>VPD + VWC / ein Controller</span></div>
      <div className="fl-chaac-grid">
        <div className="fl-chaac-copy"><h2 id="chaac-title">Chaac</h2><p className="fl-chaac-headline">Luft verstehen.<br/>Substrat im Blick.</p><p className="fl-body">Chaac bringt Klima- und Substratfunktionen zusammen. Zugeordnete externe Sensoren ergänzen die VWC-Messung. Die lokale Einrichtung verbindet Messungen mit den vorgesehenen Steuerungsaufgaben.</p><Link className="fl-link" to={`${feederHome()}#chaac-funktionen`}>Funktionen kennenlernen <span aria-hidden="true">↓</span></Link></div>
        <HardwareShowcase product="chaac"/>
      </div>
      <div className="fl-chaac-details" id="chaac-funktionen">
        <div className="fl-disclosures" aria-label="Chaac-Funktionen im Detail">{chaacFeatureGroups.map(group => <details key={group.title}><summary>{group.title}</summary><ul>{group.features.map(feature => <li key={feature}>{feature}</li>)}</ul></details>)}</div>
        <p className="fl-fineprint">VWC-Sensoren sind externe Komponenten – kein separates Controller-Produkt. Der Verlauf entsteht aus empfangenen Minutenframes auf dem Server; Übertragungslücken bleiben sichtbar.</p>
      </div>
    </section>

    <section className="fl-twin fl-section" id="gewaechshaus" aria-labelledby="twin-title">
      <div className="fl-section-label"><span>03 / Das interaktive Gewächshaus</span><span>Entdecken statt nur lesen</span></div>
      <div className="fl-section-heading"><h2 id="twin-title">Nicht nur ansehen.<br/><span>Verstehen.</span></h2><p>Öffne die Kanäle. Wähle Bauteile aus. Verfolge den Weg vom Tank bis zur Düse – und entdecke, wie die Messstrecke funktioniert.</p></div>
      <Link className="fl-twin-preview" to="/digital-twin" aria-label="Gewächshaus betreten – digitalen Twin öffnen"><img src="/feeder-assets/twin-render-front-20261026.webp" srcSet="/feeder-assets/twin-render-front-20261026-960.webp 960w, /feeder-assets/twin-render-front-20261026.webp 1774w" sizes="(max-width: 700px) 153vw, (max-width: 1000px) 90vw, (min-width: 1800px) 1560px, 88vw" width="1774" height="887" alt="Fotorealistische Vorschau des FEEDER-Erklärmodells mit drei Pflanzkanälen, Basilikum und Vorratstank" loading="lazy" decoding="async"/><span className="fl-twin-overlay"><span>Gewächshaus betreten<small>Bauteile · Messstrecke · Abläufe</small></span><b aria-hidden="true">↗</b></span></Link>
      <p className="fl-preview-caption"><span>KI-RENDERING DES 3D-ERKLÄRMODELLS</span><span>Keine Live-Messdaten · keine Steuerung deiner Anlage</span></p>
    </section>

    <section className="fl-guide fl-section" id="ratgeber" aria-labelledby="guide-title">
      <div className="fl-section-label"><span>04 / Ratgeber für deinen Einstieg</span><Link className="fl-link" to="/ratgeber">Alle Beiträge <span aria-hidden="true">↗</span></Link></div>
      <div className="fl-section-heading"><h2 id="guide-title">Gute Fragen.<br/><span>Ein klarer Einstieg.</span></h2><p>Was sagt ein Messwert wirklich? Verständliche Grundlagen mit nachvollziehbaren Quellen – ohne pauschale Erfolgsversprechen.</p></div>
      <ArticleCards articles={featured} featured/>
    </section>

    <section className="fl-portal fl-section" id="anmelden" aria-labelledby="portal-title">
      <figure className="fl-vision"><img src="/feeder-assets/aeroponic-vision.webp" alt="Illustrative Aeroponik-Farm mit Basilikum in weißen, geschlossenen Wurzelkanälen und Netztöpfen statt Erde" loading="lazy" decoding="async" width="1806" height="871"/><figcaption>Aeroponik-Farm · KI-Konzeptbild, keine Aufnahme einer realen FEEDER-Anlage</figcaption></figure>
      <div><p className="fl-kicker">05 / Dein FEEDER-Portal</p><h2 id="portal-title">Deine Geräte.<br/><span>Dein Verlauf.</span></h2><p>Öffne deine Standorte, Geräte und empfangenen Messdaten im bestehenden FEEDER-Portal.</p></div>
      <Link className="fl-button fl-portal-button" to="/anmelden">Anmelden <span aria-hidden="true">↗</span></Link>
    </section>
  </PublicShell>
}

export function FeederGuide() {
  return <PublicShell className="fm-subpage fm-guide-showcase" title="Ratgeber für Anfänger · FEEDER">
    <section className="fg-guide-hero" aria-labelledby="guide-page-title">
      <div className="fg-guide-intro">
        <p className="fg-kicker">FEEDER / Ratgeber für deinen Einstieg</p>
        <h1 id="guide-page-title">Dein Einstieg<br/><span>in den Anbau.</span></h1>
        <p className="fg-guide-lead">Was bedeuten VPD, VWC und EC? Wie funktioniert Aeroponik? Hier findest du verständliche Grundlagen mit Quellen und Grenzen – ohne pauschale Erfolgsrezepte.</p>
        <Link className="fg-button" to="/ratgeber#grundlagen">Grundlagen entdecken <span aria-hidden="true">↓</span></Link>
      </div>
      <figure className="fg-guide-image">
        <img src="/feeder-assets/landing-basil-hero.webp" alt="" decoding="async" width="1672" height="941"/>
        <figcaption>Illustrative Pflanzenvisualisierung · keine Messaufnahme</figcaption>
      </figure>
    </section>
    <section className="fg-guide-index" id="grundlagen" aria-labelledby="guide-index-title">
      <div className="fg-index-label"><span>Verstehen. Beobachten. Dazulernen.</span><span>{String(feederArticles.length).padStart(2, "0")} Beiträge</span></div>
      <div className="fg-index-heading"><h2 id="guide-index-title">Gute Fragen.<br/>Ein klarer Einstieg.</h2><p>Von Klima und Substrat bis zum Wurzelraum. Wähle die Frage, die dich gerade beschäftigt.</p></div>
      <ArticleCards/>
    </section>
  </PublicShell>
}

export function FeederArticle() {
  const { slug } = useParams()
  const article = feederArticles.find(a => a.slug === slug)
  if (!article) return <PublicShell className="fm-subpage fm-article-showcase" title="Beitrag nicht verfügbar · FEEDER">
    <section className="fg-article-missing" aria-labelledby="missing-article-title">
      <p className="fg-kicker">FEEDER / Ratgeber</p>
      <h1 id="missing-article-title">Dieser Beitrag ist nicht mehr verfügbar.</h1>
      <p>Die Ratgeber-Inhalte wurden fachlich überarbeitet. Hier findest du die aktuellen Grundlagen.</p>
      <Link className="fg-button" to="/ratgeber">Zum Ratgeber <span aria-hidden="true">↗</span></Link>
    </section>
  </PublicShell>
  return <PublicShell className="fm-subpage fm-article-showcase" title={`${article.title} · FEEDER`}>
    <article className="fg-article">
      <header className="fg-article-header">
        <Link className="fg-back" to="/ratgeber"><span aria-hidden="true">←</span> Alle Ratgeber</Link>
        <p className="fg-kicker">{article.category} · Für Einsteiger</p>
        <h1>{article.title}</h1>
        <p className="fg-article-lead">{article.excerpt}</p>
      </header>
      <div className="fg-reading-layout">
        <div className="fm-reading-body fg-reading-body">{article.content.split(/\n\s*\n/).filter(Boolean).map((paragraph, i) => <p key={i}>{paragraph}</p>)}</div>
        <aside className="fg-sources" aria-labelledby="article-sources-title">
          <p className="fg-kicker">Nachvollziehbar bleiben</p>
          <h2 id="article-sources-title">Quellen & weiterführendes Wissen</h2>
          <p>Die Quellen liefern Grundlagen und Ergebnisse für ihren jeweiligen Versuchsaufbau. Sie ersetzen keine Messung oder Validierung deines eigenen Systems.</p>
          <ul>{article.sources.map(source => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.label} <span aria-hidden="true">↗</span></a></li>)}</ul>
        </aside>
      </div>
      <div className="fg-article-next"><p>Vom Lesen<br/><span>zum Entdecken.</span></p><Link className="fg-button" to="/digital-twin">Aeroponik im Modell erkunden <span aria-hidden="true">↗</span></Link></div>
    </article>
  </PublicShell>
}

export function FeederTwinPage() {
  return <PublicShell className="fm-subpage fm-twin-showcase" title="Digitaler Gewächshaus-Twin · FEEDER">
    <section className="fs-hero fs-twin-hero" aria-labelledby="twin-page-title">
      <div className="fs-section-label"><span>FEEDER / Digitaler Twin</span><span>Lernen durch Entdecken</span></div>
      <div className="fs-hero-grid">
        <h1 id="twin-page-title">Dein Blick ins<br/><span>Gewächshaus.</span></h1>
        <div className="fs-hero-copy"><p>Erkunde die Anlage in 3D. Klicke auf ein Bauteil, um seine Funktion, Details und verfügbare Produktlinks zu sehen.</p><Link className="fs-text-link" to="/digital-twin#twin-explorer">Modell erkunden <span aria-hidden="true">↓</span></Link></div>
      </div>
      <div className="fs-twin-introduction" aria-label="Das kannst du im Twin entdecken"><span><b>01</b> Aufbau erkunden</span><span><b>02</b> Bauteile verstehen</span><span><b>03</b> Abläufe durchspielen</span></div>
    </section>
    <section className="fs-twin-workspace" id="twin-explorer" aria-label="Gewächshaus erkunden"><GreenhouseTwin compact/></section>
    <section className="fs-next fm-twin-reading" aria-labelledby="twin-reading-title">
      <div><p className="fs-kicker">Vom Modell zum Wissen</p><h2 id="twin-reading-title">Und wie fein<br/>ist der <span>Nebel?</span></h2></div>
      <div><p>0,2 mm beschreibt die Düsenöffnung, nicht die Tröpfchengröße. Auch die 20-bar-Rechenbasis bestätigt keine Tropfenverteilung: Welche Tröpfchen an den Wurzeln ankommen, muss gemessen werden.</p><Link className="fs-text-link" to="/post/aeroponik-tropfengroesse-luftfeuchte-bewaesserung">Zum Ratgeber über Tröpfchengröße <span aria-hidden="true">↗</span></Link></div>
    </section>
  </PublicShell>
}
